import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../components/Logo";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { confirmPasswordReset } from "../../api/authProfessional";
import { RequiredMark } from "../../components/RequiredMark";

const strongPassword = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const PENDING_RESET_EMAIL_KEY = "agbaza.pro.reset.email";
const PENDING_RESET_OTP_KEY = "agbaza.pro.reset.otp";

type NewPasswordState = {
  email?: string;
  otpCode?: string;
};

export default function ProNewPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const validPassword = useMemo(() => strongPassword.test(password), [password]);
  const match = confirm === password && confirm.length > 0;
  const state = (location.state as NewPasswordState | null) ?? null;

  useEffect(() => {
    const resolvedEmail = state?.email ?? sessionStorage.getItem(PENDING_RESET_EMAIL_KEY) ?? "";
    const resolvedOtp = state?.otpCode ?? sessionStorage.getItem(PENDING_RESET_OTP_KEY) ?? "";
    if (!resolvedEmail || !resolvedOtp) {
      navigate("/professionnel/mot-de-passe/oubli", { replace: true });
      return;
    }
    setEmail(resolvedEmail);
    setOtpCode(resolvedOtp);
  }, [navigate, state?.email, state?.otpCode]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validPassword || !match) return;
    setError("");
    setSubmitting(true);
    const result = await confirmPasswordReset({ otpCode, newPassword: password });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message || "Réinitialisation impossible.");
      return;
    }
    sessionStorage.removeItem(PENDING_RESET_EMAIL_KEY);
    sessionStorage.removeItem(PENDING_RESET_OTP_KEY);
    navigate("/professionnel/connexion", {
      replace: true,
      state: { message: "Mot de passe réinitialisé. Connectez-vous." },
    });
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img
        src={import.meta.env.BASE_URL + 'maps-reset-side.jpg'}
        alt="pro-newpwd-bg"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30" />

      <div className="absolute inset-0 z-10 px-4 md:px-8 py-6 md:py-8 flex">
        <div className="max-w-6xl mx-auto flex flex-col w-full">
          <div className="mt-auto flex items-end justify-between gap-6 pl-2 md:pl-6 pr-4 md:pr-8 pb-4 md:pb-8">
            <div className="w-full max-w-2xl min-h-[560px] bg-white/95 backdrop-blur rounded-[12px] shadow p-8 md:p-10">
              <h1 className="text-2xl font-semibold" style={{ color: '#0B1F16' }}>Recuperation du mot passe</h1>
              {error && (
                <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
                  {error}
                </div>
              )}
              <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Nouveau mot de passe
                    <RequiredMark />
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className={`w-full h-11 rounded-[10px] border-2 px-3 ${validPassword || password === '' ? '' : 'border-red-400'}`}
                    style={{ borderColor: validPassword || password === '' ? '#D1E2DD' : '#F87171' }}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Confirmer mot de passe
                    <RequiredMark />
                  </label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                    className={`w-full h-11 rounded-[10px] border-2 px-3 ${match || confirm === '' ? '' : 'border-red-400'}`}
                    style={{ borderColor: match || confirm === '' ? '#D1E2DD' : '#F87171' }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!validPassword || !match || !email || !otpCode || submitting}
                  className="w-full h-11 rounded-[10px] font-medium cursor-pointer disabled:opacity-60"
                  style={{ backgroundColor: '#00553E', color: '#FFFFFF' }}
                >
                  {submitting ? "Confirmation..." : "Confirmer"}
                </button>
                <Link to="/professionnel/connexion" className="block">
                  <button type="button" className="w-full h-11 rounded-[10px] font-medium border-2 cursor-pointer" style={{ borderColor: '#D1E2DD', color: '#00553E' }}>
                    Retour
                  </button>
                </Link>
              </form>
            </div>
            <div className="hidden md:block shrink-0">
              <img src={import.meta.env.BASE_URL + 'Group 5.png'} alt="agabaza-logo" className="w-auto" style={{ height: 144 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

